// Enhanced Google Analytics 4 tracking with detailed click coordinates and session data
class EnhancedAnalytics {
  constructor() {
    this.sessionId = null;
    this.pageStartTime = null;
    this.clickSequence = 0;
    this.scrollDepth = 0;
    this.isInitialized = false;
    this.init();
  }

  init() {
    // Generate unique session ID
    this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.pageStartTime = Date.now();
    
    // Check if gtag is available
    if (typeof window !== 'undefined' && window.gtag) {
      this.isInitialized = true;
      console.log('Enhanced Analytics initialized with session:', this.sessionId);
      
      // Set up event listeners
      this.setupEventListeners();
      
      // Track initial page view with session data
      this.trackPageView();
    } else {
      console.warn('Google Analytics not available');
    }
  }

  setupEventListeners() {
    // Track all clicks with detailed information
    document.addEventListener('click', (event) => {
      this.trackDetailedClick(event);
    });

    // Track scroll depth
    let maxScrollDepth = 0;
    window.addEventListener('scroll', () => {
      const scrolled = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100;
      const roundedScroll = Math.round(scrolled / 10) * 10; // Round to nearest 10%
      
      if (roundedScroll > maxScrollDepth) {
        maxScrollDepth = roundedScroll;
        this.scrollDepth = Math.min(roundedScroll, 100);
        
        this.trackEvent('scroll_depth', {
          session_id: this.sessionId,
          scroll_depth: this.scrollDepth,
          page_path: window.location.pathname,
          time_on_page: Date.now() - this.pageStartTime
        });
      }
    });



    // Track window blur/focus
    window.addEventListener('blur', () => {
      this.trackEvent('window_blur', {
        session_id: this.sessionId,
        page_path: window.location.pathname,
        time_on_page: Date.now() - this.pageStartTime
      });
    });

    window.addEventListener('focus', () => {
      this.trackEvent('window_focus', {
        session_id: this.sessionId,
        page_path: window.location.pathname,
        time_on_page: Date.now() - this.pageStartTime
      });
    });
  }

  trackDetailedClick(event) {
    if (!this.isInitialized) return;
    
    this.clickSequence++;
    const element = event.target;
    
    // Get detailed element information
    const elementData = {
      session_id: this.sessionId,
      click_sequence: this.clickSequence,
      element_type: element.tagName,
      element_id: element.id || '',
      element_class: element.className || '',
      element_text: element.innerText?.substring(0, 100) || '',
      element_html: element.outerHTML?.substring(0, 200) || '',
      page_x: event.pageX,
      page_y: event.pageY,
      screen_x: event.screenX,
      screen_y: event.screenY,
      page_path: window.location.pathname,
      scroll_position: window.scrollY,
      time_on_page: Date.now() - this.pageStartTime,
      timestamp: new Date().toISOString()
    };

    // Track specific element types with custom events
    if (element.tagName === 'BUTTON') {
      this.trackEvent('button_click', {
        ...elementData,
        button_text: element.innerText?.substring(0, 50) || '',
        button_type: element.type || 'button'
      });
    } else if (element.tagName === 'A') {
      this.trackEvent('link_click', {
        ...elementData,
        link_url: element.href || '',
        link_text: element.innerText?.substring(0, 50) || '',
        is_external: element.href ? !element.href.includes(window.location.hostname) : false
      });
    } else {
      this.trackEvent('element_click', elementData);
    }

    // Also track as general click event for GA4's automatic reporting
    this.trackEvent('click', {
      session_id: this.sessionId,
      click_coordinates: `${event.pageX},${event.pageY}`,
      element_type: element.tagName,
      page_path: window.location.pathname
    });
  }

  trackPageView(pagePath = null, pageTitle = null) {
    if (!this.isInitialized) return;
    
    const currentPath = pagePath || window.location.pathname;
    const currentTitle = pageTitle || document.title;
    
    // Reset page-specific tracking
    this.pageStartTime = Date.now();
    this.clickSequence = 0;
    this.scrollDepth = 0;
    
    // Track page view with session data
    window.gtag('config', 'G-KXPPK050SC', {
      page_path: currentPath,
      page_title: currentTitle,
      custom_map: {
        'custom_parameter_1': 'session_id'
      }
    });

    // Track detailed page view event
    this.trackEvent('page_view_detailed', {
      session_id: this.sessionId,
      page_path: currentPath,
      page_title: currentTitle,
      referrer: document.referrer,
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      window_size: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString()
    });
  }

  trackEvent(eventName, parameters = {}) {
    if (!this.isInitialized) return;
    
    // Ensure all parameters are strings or numbers for GA4
    const cleanParams = {};
    Object.keys(parameters).forEach(key => {
      const value = parameters[key];
      if (typeof value === 'string' || typeof value === 'number') {
        cleanParams[key] = value;
      } else if (value !== null && value !== undefined) {
        cleanParams[key] = String(value);
      }
    });
    
    window.gtag('event', eventName, cleanParams);
    console.log(`GA4 Event: ${eventName}`, cleanParams);
  }

  // Fish Finder specific tracking methods
  trackProductView(productId, productName, category, price = null) {
    this.trackEvent('view_item', {
      session_id: this.sessionId,
      item_id: productId,
      item_name: productName,
      item_category: category,
      value: price,
      currency: 'USD'
    });
  }

  trackDealClick(dealName, retailer, originalPrice, salePrice, dealUrl) {
    const savings = originalPrice - salePrice;
    
    this.trackEvent('deal_click', {
      session_id: this.sessionId,
      deal_name: dealName,
      retailer: retailer,
      original_price: originalPrice,
      sale_price: salePrice,
      savings_amount: savings,
      savings_percent: Math.round((savings / originalPrice) * 100),
      deal_url: dealUrl
    });
  }

  trackBundleBuilderStep(step, componentType, componentName) {
    this.trackEvent('bundle_builder_step', {
      session_id: this.sessionId,
      builder_step: step,
      component_type: componentType,
      component_name: componentName,
      step_number: step
    });
  }

  trackSearch(searchTerm, resultsCount) {
    this.trackEvent('search', {
      session_id: this.sessionId,
      search_term: searchTerm,
      results_count: resultsCount
    });
  }

  trackPriceComparison(productName, lowestPrice, highestPrice, retailerCount) {
    this.trackEvent('price_comparison_view', {
      session_id: this.sessionId,
      product_name: productName,
      lowest_price: lowestPrice,
      highest_price: highestPrice,
      retailer_count: retailerCount,
      price_range: highestPrice - lowestPrice
    });
  }

  // Get session analytics summary (for debugging)
  getSessionSummary() {
    return {
      sessionId: this.sessionId,
      timeOnPage: Date.now() - this.pageStartTime,
      clickSequence: this.clickSequence,
      scrollDepth: this.scrollDepth,
      currentPage: window.location.pathname
    };
  }
}

// Create global instance
const enhancedAnalytics = new EnhancedAnalytics();

export default enhancedAnalytics; 